import { Routes, Route, Navigate } from "react-router-dom";
import { ProtectedRoute } from "./auth/ProtectedRoute";
import { useAuth } from "./auth/AuthContext";
import { AppShell } from "./components/AppShell";
import { LoginPage } from "./features/auth/LoginPage";
import { PasswordTokenPage } from "./features/auth/PasswordTokenPage";
import { MagicLoginPage } from "./features/auth/MagicLoginPage";
import { JobsPage } from "./features/jobs/JobsPage";
import { ClientsPage } from "./features/clients/ClientsPage";
import { UsersPage } from "./features/admin/UsersPage";
import { EmailLogPage } from "./features/admin/EmailLogPage";
import { EmailTemplatesAdminPage } from "./features/admin/EmailTemplatesAdminPage";
import { DocumentSettingsPage } from "./features/admin/DocumentSettingsPage";
import { PortalPage } from "./features/portal/PortalPage";
import { DocumentsPage } from "./features/documents/DocumentsPage";
import { InvoiceCreatePage } from "./features/documents/InvoiceCreatePage";
import { AccountPage } from "./features/auth/AccountPage";
import { SchedulingPage } from "./features/scheduling/SchedulingPage";

/**
 * Sends the user to their landing page: portal users to /portal, everyone
 * else to the vendor board.
 */
function HomeRedirect() {
  const { user } = useAuth();
  const canRead = user?.permissions.includes("*") || user?.permissions.includes("jobs.read");
  return <Navigate to={user?.systemRole === "client" ? "/portal" : canRead ? "/jobs" : "/account"} replace />;
}

/**
 * Application router.
 *
 * - `/login` is public and renders the sign-in form.
 * - Every other route is wrapped in {@link ProtectedRoute}, which redirects
 *   unauthenticated users to `/login`. Vendor pages render inside
 *   {@link AppShell}; `/portal` is the client-facing surface (client users are
 *   scoped server-side via their organisation link).
 */
export function App() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/set-password" element={<PasswordTokenPage mode="set" />} />
      <Route path="/reset-password" element={<PasswordTokenPage mode="reset" />} />
      <Route path="/magic-login" element={<MagicLoginPage />} />
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/account" element={<AccountPage />} />
          <Route path="/jobs" element={<JobsPage />} />
          <Route path="/schedule" element={<SchedulingPage />} />
          <Route path="/invoices" element={<DocumentsPage />} />
          <Route path="/invoices/create" element={<InvoiceCreatePage />} />
          <Route path="/clients" element={<ClientsPage />} />
          <Route path="/admin/users" element={<UsersPage />} />
          <Route path="/admin/email-log" element={<EmailLogPage />} />
          <Route path="/admin/email-templates" element={<EmailTemplatesAdminPage />} />
          <Route path="/admin/documents" element={<DocumentSettingsPage />} />
          <Route path="/" element={<HomeRedirect />} />
        </Route>
        <Route path="/portal" element={<PortalPage />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
